// src/resources/teams.js
import * as React from 'react';
import { useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    required,
    usePermissions,
    useRedirect,
    DeleteButton,
    Toolbar,
    SaveButton,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
} from 'react-admin';
import { Tooltip } from '@mui/material';
import { useResourceLimits, useResourceLimitMessage } from '../hooks/useResourceLimits';

const TeamTitle = ({ record }) => {
    return <span>Team: {record ? record.name : ''}</span>;
};

// Custom actions with limit-aware create button for teams
const TeamListActions = () => {
    const { permissions } = usePermissions();
    const { data: limitsData, invalidate } = useResourceLimits('teams');
    const limitMessage = useResourceLimitMessage('teams');
    
    // Invalidate limits when the list might have changed
    React.useEffect(() => {
        const handleStorageChange = () => {
            invalidate();
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [invalidate]);
    
    // Only show create button for tenant_admin
    if (permissions !== 'tenant_admin') {
        return (
            <TopToolbar>
                <RefreshButton onClick={invalidate} />
                <ExportButton />
            </TopToolbar>
        );
    }
    
    const createButton = (
        <CreateButton 
            disabled={limitsData && !limitsData.canCreate}
            sx={{
                ...(limitsData && !limitsData.canCreate && {
                    opacity: 0.5,
                    cursor: 'not-allowed'
                })
            }}
        />
    );
    
    return (
        <TopToolbar>
            <RefreshButton onClick={invalidate} />
            {limitsData && !limitsData.canCreate ? (
                <Tooltip title={limitMessage} placement="top">
                    <span>{createButton}</span>
                </Tooltip>
            ) : (
                createButton
            )}
            <ExportButton />
        </TopToolbar>
    );
};

const TeamList = () => {
    const { permissions } = usePermissions();

    // Only tenant_admin should see this page
    if (permissions !== 'tenant_admin') {
        return <div>You don't have permission to view teams.</div>;
    }
    
    return (
        <List 
            bulkActionButtons={false}
            sort={{ field: 'name', order: 'ASC' }}
            actions={<TeamListActions />}
        >
            <Datagrid 
                rowClick="edit" 
                bulkActionButtons={false} 
                sx={{ 
                    '& [data-field="__selection__"]': { display: 'none' },
                    '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                    // Making column headers bold
                    '& .MuiTableCell-head': {
                        fontWeight: 'bold',
                    }
                }}
            >
                <TextField source="name" sortable={true} />
            </Datagrid>
        </List>
    );
};

const TeamEditToolbar = () => (
    <Toolbar>
        <SaveButton />
        <DeleteButton redirect="list" />
    </Toolbar>
);

const TeamEdit = () => {
    const redirect = useRedirect();
    const { permissions } = usePermissions();

    // Only tenant_admin should see this page
    if (permissions !== 'tenant_admin') {
        redirect('/dashboard');
        return null;
    }
    
    return (
        <Edit title={<TeamTitle />} mutationMode="pessimistic" redirect="list">
            <SimpleForm toolbar={<TeamEditToolbar />}>
                <TextInput source="name" validate={[required()]} fullWidth />
            </SimpleForm>
        </Edit>
    );
};

const TeamCreate = () => {
    const redirect = useRedirect();
    const { permissions } = usePermissions();

    // Check permissions on component mount
    useEffect(() => {
        // If not tenant_admin, redirect
        if (permissions !== 'tenant_admin') {
            redirect('/dashboard');
        }
    }, [permissions, redirect]);

    // For users without tenant_admin permission, we'll return null,
    // but the useEffect above will handle the redirect
    if (permissions !== 'tenant_admin') {
        return null;
    }

    return (
        <Create redirect="list">
            <SimpleForm>
                <TextInput source="name" validate={[required()]} fullWidth />
            </SimpleForm>
        </Create>
    );
};

export { TeamList, TeamEdit, TeamCreate };